// Lib
import { DependencyList, useCallback, useEffect, useState, SyntheticEvent } from 'react';
import { createDispatchHook, createSelectorHook, useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import {
    EditorInstanceObject,
    TiptapEditorRegistryCallback,
    TiptapEditorStoreActions,
    TiptapEditorStoreState,
} from './tiptapEditorStoreTypes';

// Context
import { TiptapEditorStoreContext } from './TiptapEditorStoreProvider';

// Actions
import { getActiveTiptapEditorThunk, getTiptapEditorByIdThunk } from './tiptapEditorStoreActions';
import { Editor } from '@tiptap/react';
import { getIsTouchSelector } from '../../../platform/platformSelector';

// FIXME-Tiptap thunk typings
export const useTiptapEditorDispatch = createDispatchHook<TiptapEditorStoreState, TiptapEditorStoreActions>(
    TiptapEditorStoreContext,
);

const tiptapSelectorHook = createSelectorHook<TiptapEditorStoreState, TiptapEditorStoreActions>(
    TiptapEditorStoreContext,
);

/**
 * Selects a value from a Tiptap editor instance.
 *
 * An editor is a stable reference, so a memoised selector (`createSelector`) given only the editor
 * would never re-run. The hooks therefore pass a `cacheKey` that changes whenever the editor's
 * content does. It sits third because `createSelector` treats every argument after the first as a
 * further input, and the hooks have nothing to put in the second (props) position.
 *
 * Both trailing arguments are optional, so a plain selector can declare - and be called with -
 * the editor alone.
 */
export type ActiveTiptapEditorSelector = (editor: Editor | undefined, props?: undefined, cacheKey?: unknown) => any;

/**
 * This hook allows consumers to select values from the active tiptap editor, without needing
 * to worry about finding the instance within the store's structure.
 *
 * The use of "activeEditor.instance" is so that we can ensure a re-render on active editor changes.
 * The activeEditor is a stable reference, so we need to wrap it in an object to ensure the reference changes.
 *
 * This hook allows selectors to ignore this detail.
 */
export const useTiptapActiveEditorSelector = (selector: ActiveTiptapEditorSelector) =>
    // The activeEditor wrapper is rebuilt on every dispatch, so it serves as the cache key.
    tiptapSelectorHook((state) => selector(state.activeEditor?.instance, undefined, state.activeEditor));

/**
 * Runs an editor selector against a registry editor, using its state as the cache key - a new
 * object on every transaction, where the editor instance itself never changes.
 */
const runEditorSelector = (selector: ActiveTiptapEditorSelector, editor: Editor | undefined) =>
    selector(editor, undefined, editor?.state);

/**
 * Selects from a mounted editor resolved by its editor ID, re-running on that editor's
 * transactions.
 *
 * `useTiptapActiveEditorSelector` only reaches the editor being edited. This reaches one that is
 * merely mounted - a selected task list, for instance - so the toolbar can read its live document
 * without the user editing it.
 *
 * Keeps the previous value when a transaction leaves the selected value deeply equal, so consumers
 * only re-render on a change they care about. This mirrors Tiptap's own `useEditorState`, which
 * cannot be used here: it snapshots the editor when it mounts and only refreshes that snapshot on
 * a transaction, so an editor that registers later would stay stale.
 *
 * The selector must be stable (a module-level function), since it re-subscribes when it changes.
 */
export const useTiptapEditorByIdSelector = (editorId: string | undefined, selector: ActiveTiptapEditorSelector) => {
    const editor = tiptapSelectorHook((state) => (editorId ? state.editorsById[editorId] : undefined))?.instance;
    const [selected, setSelected] = useState(() => runEditorSelector(selector, editor));

    useEffect(() => {
        const updateSelected = () =>
            setSelected((previous: unknown) => {
                const next = runEditorSelector(selector, editor);
                return isEqual(previous, next) ? previous : next;
            });

        updateSelected();

        // `transaction` fires on every dispatch, after the new state is applied, so it covers
        // `update` and `selectionUpdate` too.
        editor?.on('transaction', updateSelected);
        return () => {
            editor?.off('transaction', updateSelected);
        };
    }, [editor, selector]);

    return selected;
};

/**
 * This hook allows the consumer to get the active Tiptap editor instance directly,
 * however it is marked as "dangerous" because it could cause performance issues if it's used
 * in components that are regularly rendered.
 *
 * This is because the active editor instance can change frequently, so if many components use
 * this, it could cause many unnecessary re-renders.
 */
export const dangerouslyUseTiptapActiveEditor = () => tiptapSelectorHook((state) => state.activeEditor)?.instance;

type ActiveTiptapEditorCallback<Args extends unknown[], Result> = (
    editor?: Editor | undefined,
    ...args: Args
) => Result;

/**
 * Fires a thunk to get the active tiptap editor from the tiptap editor store,
 * then provides the editor to the callback function.
 */
export const useActiveTiptapEditorCallback = <Args extends unknown[] = unknown[], Result = unknown>(
    func: ActiveTiptapEditorCallback<Args, Result>,
    dependencies?: DependencyList,
): ((...args: Args) => Result) => {
    const dispatch = useTiptapEditorDispatch();

    const returnFunc = (...args: Args): Result => {
        // @ts-expect-error Tiptap thunk typings - FIXME
        const activeEditor = dispatch(getActiveTiptapEditorThunk()) as EditorInstanceObject | null;
        return func(activeEditor?.instance, ...args);
    };

    return dependencies ? useCallback(returnFunc, dependencies) : returnFunc;
};

/**
 * Returns a callback that resolves a Tiptap editor instance by its editor ID.
 *
 * This is useful if the editor isn't the active editor, so the activeEditor state isn't set,
 * but we still need a reference to the Tiptap editor, for some reason.
 * Such as when removing labels from task items - selecting the label chip will mean the
 * editor is no longer active.
 */
export const useTiptapEditorByIdCallback = (): TiptapEditorRegistryCallback => {
    const dispatch = useTiptapEditorDispatch();

    return useCallback(
        (editorId: string) => {
            // @ts-expect-error Tiptap thunk typings
            const editor = dispatch(
                // @ts-expect-error Tiptap thunk typings
                getTiptapEditorByIdThunk(editorId),
            ) as EditorInstanceObject | null;
            return editor?.instance;
        },
        [dispatch],
    );
};

type ActiveTiptapEditorResolvedCallback = (editor: Editor, ...args: any[]) => unknown;
export const useActiveTiptapEditorTouchCallback = (
    callback: ActiveTiptapEditorResolvedCallback,
    dependencies: DependencyList,
) => {
    const isTouchDevice = useSelector(getIsTouchSelector);

    return useActiveTiptapEditorCallback(
        (editor, ...args) => {
            if (!editor) return;

            const result = callback(editor, ...args);

            if (isTouchDevice) editor.commands.focus(undefined, { scrollIntoView: false });

            return result;
        },
        [isTouchDevice, ...(dependencies || [])],
    );
};

export const useFocusActiveTiptapEditorOnTouch = (dependencies?: DependencyList) => {
    const isTouchDevice = useSelector(getIsTouchSelector);

    return useActiveTiptapEditorCallback(
        (editor, _isOpen: boolean, event?: SyntheticEvent) => {
            if (!editor || !event || !isTouchDevice) return;

            editor.commands.focus(undefined, { scrollIntoView: false });
        },
        [isTouchDevice, ...(dependencies || [])],
    );
};
